import { useCallback } from "react"

import type { QueryHrefParams } from "@/hooks/useQueryParams"
import type { RunRowLike } from "@/types/RunRow"

/**
 * Where a run-result row points: the result's own page, used both for row
 * navigation and for the row menu's Copy URL and Copy ID.
 *
 * A row with no `simulation_output_id` stands in for a simulation that never
 * produced a result, so it has no page and nothing to copy.
 */
export function useRunResultRowHrefs({
  queryParams,
  runId,
  slug,
}: {
  queryParams: QueryHrefParams
  runId: string
  slug: string
}) {
  const getResultHref = useCallback(
    (simulationOutputId: string) =>
      queryParams.getHref(
        `/simulated/runs/${runId}/results/${simulationOutputId}`,
        slug,
      ),
    [queryParams, runId, slug],
  )

  const rowCopyId = useCallback(
    (row: RunRowLike) => row.simulation_output_id ?? undefined,
    [],
  )

  const rowHref = useCallback(
    (row: RunRowLike) =>
      row.simulation_output_id
        ? getResultHref(row.simulation_output_id)
        : undefined,
    [getResultHref],
  )

  return { getResultHref, rowCopyId, rowHref }
}
